import React, { useContext, useState } from 'react';
import { RicosContext } from 'ricos-context';
import { SettingsSection, SliderWithInput, SettingsSeparator } from 'wix-rich-content-ui-components';
import { s as styles, T as ToggledColorPicker } from '../toggled-color-picker-07116f3d.js';
import { BORDER_WIDTH_INPUT, CORNER_RADIUS_INPUT, TEXT_COLOR_TOGGLE, BORDER_COLOR_TOGGLE, BACKGROUND_COLOR_TOGGLE } from '../dataHooks.js';
import { DEFAULT_PALETTE } from '../defaults.js';
import { isDefined } from '../utils/isDefined.js';
import 'wix-rich-content-plugin-commons';
import '../style-inject.es-746bb8ed.js';
import './color-toggle-component.js';
import 'classnames';

var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
const DesignComponent = ({
  stylesData,
  settings,
  onChange
}) => {
  const { languageDir, t } = useContext(RicosContext);
  const [openedPicker, setOpenedPicker] = useState(null);
  const resolveColors = (currentColor, getColorFn) => {
    var _a;
    const settingsColors = settings.colors ? Object.values(settings.colors) : [];
    const resolvedColors = [
      ...DEFAULT_PALETTE,
      ...settingsColors,
      ...(_a = getColorFn == null ? void 0 : getColorFn()) != null ? _a : [],
      currentColor
    ].filter(isDefined).map((c) => c.toUpperCase()).slice(-12);
    return Array.from(new Set(resolvedColors));
  };
  const [userColors, setUserColors] = useState(() => ({
    textColors: resolveColors(stylesData.colors.text, settings.getTextColors),
    borderColors: resolveColors(stylesData.colors.border, settings.getBorderColors),
    backgroundColors: resolveColors(stylesData.colors.background, settings.getBackgroundColors)
  }));
  const handleOnChange = (data) => {
    onChange({
      border: __spreadValues(__spreadValues({}, stylesData.border), data.border),
      colors: __spreadValues(__spreadValues({}, stylesData.colors), data.colors)
    });
  };
  return /* @__PURE__ */ React.createElement("div", { className: styles.button_designComponent_design_component }, /* @__PURE__ */ React.createElement(SettingsSection, { ariaProps: { "aria-label": "border selection", role: "region" } }, /* @__PURE__ */ React.createElement("div", { className: styles.button_designComponent_row }, /* @__PURE__ */ React.createElement("div", { className: styles.button_designComponent_section_header_border }, t("ButtonModal_Border_Section")), /* @__PURE__ */ React.createElement("div", { className: styles.button_designComponent_input_container_width }, /* @__PURE__ */ React.createElement(
    SliderWithInput,
    {
      defaultValue: stylesData.border.width,
      min: 0,
      max: 15,
      label: t("ButtonModal_Width_Input"),
      onChange: (width) => handleOnChange({ border: { width } }),
      languageDir,
      inputDataHook: BORDER_WIDTH_INPUT
    }
  )), /* @__PURE__ */ React.createElement("div", { className: styles.button_designComponent_input_container_corner }, /* @__PURE__ */ React.createElement(
    SliderWithInput,
    {
      defaultValue: stylesData.border.radius,
      min: 0,
      max: 30,
      label: t("ButtonModal_Radius_Input"),
      onChange: (radius) => handleOnChange({ border: { radius } }),
      languageDir,
      inputDataHook: CORNER_RADIUS_INPUT
    }
  )))), /* @__PURE__ */ React.createElement(SettingsSeparator, { top: true, bottom: true }), /* @__PURE__ */ React.createElement(SettingsSection, { ariaProps: { "aria-label": "color selection", role: "region" } }, /* @__PURE__ */ React.createElement("div", { className: styles.button_designComponent_colorPicker_container }, /* @__PURE__ */ React.createElement("div", { className: styles.button_designComponent_section_header_color }, t("ButtonModal_Color_Section")), /* @__PURE__ */ React.createElement(
    ToggledColorPicker,
    {
      color: stylesData.colors.text,
      isExpanded: openedPicker === "textColor",
      onToggle: (value) => setOpenedPicker(value ? "textColor" : null),
      label: t("ButtonModal_Text_Color"),
      userColors: userColors.textColors,
      onNewColorAdded: (color) => {
        var _a;
        (_a = settings.onTextColorAdded) == null ? void 0 : _a.call(settings, color);
        setUserColors((prev) => __spreadProps(__spreadValues({}, prev), { textColors: [...prev.textColors, color] }));
      },
      onChange: (text) => handleOnChange({ colors: { text } }),
      dataHook: TEXT_COLOR_TOGGLE
    }
  ), /* @__PURE__ */ React.createElement(
    ToggledColorPicker,
    {
      color: stylesData.colors.border,
      isExpanded: openedPicker === "borderColor",
      onToggle: (value) => setOpenedPicker(value ? "borderColor" : null),
      label: t("ButtonModal_Border_Color"),
      userColors: userColors.borderColors,
      onNewColorAdded: (color) => {
        var _a;
        (_a = settings.onBorderColorAdded) == null ? void 0 : _a.call(settings, color);
        setUserColors((prev) => __spreadProps(__spreadValues({}, prev), { borderColors: [...prev.borderColors, color] }));
      },
      onChange: (border) => handleOnChange({ colors: { border } }),
      dataHook: BORDER_COLOR_TOGGLE
    }
  ), /* @__PURE__ */ React.createElement(
    ToggledColorPicker,
    {
      color: stylesData.colors.background,
      isExpanded: openedPicker === "backgroundColor",
      onToggle: (value) => setOpenedPicker(value ? "backgroundColor" : null),
      label: t("ButtonModal_Background_Color"),
      userColors: userColors.backgroundColors,
      onNewColorAdded: (color) => {
        var _a;
        (_a = settings.onBackgroundColorAdded) == null ? void 0 : _a.call(settings, color);
        setUserColors((prev) => __spreadProps(__spreadValues({}, prev), {
          backgroundColors: [...prev.backgroundColors, color]
        }));
      },
      onChange: (background) => handleOnChange({ colors: { background } }),
      dataHook: BACKGROUND_COLOR_TOGGLE
    }
  ))));
};

export { DesignComponent, DesignComponent as default };
//# sourceMappingURL=design-component.js.map
